import { Context, Data, Effect, Optic, Option, Result, Schema } from 'effect';

import { ErrorTag } from '../../data/error';
import { ContextTag } from '../context';
import { PickupCarrier, PickupPointSchema, type PickupPoint } from '../shipping/PickupPoint';

export const PPL_WIDGET_LOADER_URL = 'https://www.ppl.cz/accesspointwidget/loader.js';
export const PPL_WIDGET_TAG = 'ppl-access-point-widget';
export const PPL_WIDGET_EVENT = 'ppl-accesspointwidget-select';
export const PPL_WIDGET_CLOSE_EVENT = 'ppl-accesspointwidget-close';
export const PPL_WIDGET_ERROR_EVENT = 'ppl-accesspointwidget-error';
export const PPL_WIDGET_READY_EVENT = 'ppl-accesspointwidget-ready';
export const PPL_WIDGET_ASSET_MARKER = 'data-ppl-widget-loader';
export const PPL_WIDGET_EMBED_ID = 'ppl-access-point-embed';

const nullable = Schema.OptionFromOptionalNullOr;

const PplIntervalSchema = Schema.Struct({
  from: nullable(Schema.String),
  to: nullable(Schema.String),
});

const PplRegularHoursSchema = Schema.Struct({
  day: nullable(Schema.Finite),
  open: nullable(Schema.Boolean),
  nonstop: nullable(Schema.Boolean),
  intervals: nullable(Schema.Array(PplIntervalSchema)),
});

const PplAvailabilitySchema = Schema.Struct({ available: nullable(Schema.Boolean) });

// PPL documents the exceptional array without documenting its entry shape.
// The schema preserves each entry until PPL publishes that shape.
export const PplSelectionSchema = Schema.Struct({
  code: nullable(Schema.NonEmptyString),
  name: nullable(Schema.NonEmptyString),
  type: nullable(Schema.String),
  address: nullable(
    Schema.Struct({
      street: nullable(Schema.NonEmptyString),
      city: nullable(Schema.NonEmptyString),
      zipCode: nullable(Schema.NonEmptyString),
      countryCode: nullable(Schema.NonEmptyString),
      gps: nullable(
        Schema.Struct({
          lat: nullable(Schema.Finite),
          lon: nullable(Schema.Finite),
        })
      ),
    })
  ),
  openHours: nullable(
    Schema.Struct({
      regular: nullable(Schema.Array(PplRegularHoursSchema)),
      exceptional: nullable(Schema.Array(Schema.Unknown)),
    })
  ),
  paymentMethods: nullable(
    Schema.Struct({
      cash: nullable(Schema.Boolean),
      card: nullable(Schema.Boolean),
      onlineRequired: nullable(Schema.Boolean),
    })
  ),
  additionalNote: nullable(Schema.String),
  accessibility: nullable(
    Schema.Struct({
      parking: nullable(PplAvailabilitySchema),
      publicTransport: nullable(PplAvailabilitySchema),
      walk: nullable(PplAvailabilitySchema),
    })
  ),
  activeFeatures: nullable(
    Schema.Struct({
      map: nullable(Schema.Boolean),
      dropOff: nullable(Schema.Boolean),
      pickUpOrder: nullable(Schema.Boolean),
      vacationMode: nullable(Schema.Boolean),
    })
  ),
  capacityStatus: nullable(Schema.String),
  acceptedSizes: nullable(Schema.Array(Schema.String)),
  newAccessPoint: nullable(Schema.Boolean),
  urlPath: nullable(Schema.String),
  // These fields occur in a live selection but are absent from the API reference.
  externalId: nullable(Schema.String),
  accessPointType: nullable(Schema.String),
  barrierFree: nullable(Schema.Boolean),
  distance: nullable(Schema.Finite),
});

export type PplSelection = Schema.Schema.Type<typeof PplSelectionSchema>;
export type PplT = PplSelection;

const addressOptic = Optic.id<PplSelection>().key('address').compose(Optic.some());
const streetOptic = addressOptic.key('street').compose(Optic.some());
const cityOptic = addressOptic.key('city').compose(Optic.some());
const zipOptic = addressOptic.key('zipCode').compose(Optic.some());
const countryOptic = addressOptic.key('countryCode').compose(Optic.some());

export const selectionCountry = (point: PplSelection): string | undefined =>
  Result.getOrUndefined(Optic.getResult(point, countryOptic));

export const toPickupPoint = (point: PplT): Effect.Effect<PickupPoint, PplWidgetPayloadError> =>
  Option.match(point.code, {
    onNone: () =>
      Effect.fail(
        new PplWidgetPayloadError({
          message: 'PPL selection is missing "code". The backend requires it as "placementId".',
        })
      ),
    onSome: (code) => {
      const street = Result.getOrUndefined(Optic.getResult(point, streetOptic));
      const city = Result.getOrUndefined(Optic.getResult(point, cityOptic));
      const zip = Result.getOrUndefined(Optic.getResult(point, zipOptic));
      return Schema.decodeEffect(PickupPointSchema)({
        carrier: PickupCarrier.Ppl,
        placementId: code,
        name: Option.getOrElse(point.name, () => code),
        ...(street === undefined ? {} : { street }),
        ...(city === undefined ? {} : { city }),
        ...(zip === undefined ? {} : { zip }),
      }).pipe(
        Effect.mapError(
          (cause) =>
            new PplWidgetPayloadError({
              message: 'PPL selection has invalid checkout fields',
              cause,
            })
        )
      );
    },
  });

export class PplWidgetNotLoadedError extends Data.TaggedError(ErrorTag.PplWidgetNotLoadedError)<{
  message: string;
}> {}

export class PplWidgetCancelledError extends Data.TaggedError(ErrorTag.PplWidgetCancelledError)<{
  message: string;
}> {}

export class PplWidgetAssetLoadError extends Data.TaggedError(ErrorTag.PplWidgetAssetLoadError)<{
  message: string;
  cause?: unknown;
}> {}

export class PplWidgetPayloadError extends Data.TaggedError(ErrorTag.PplWidgetPayloadError)<{
  message: string;
  cause?: unknown;
}> {}

export class PplWidgetRuntimeError extends Data.TaggedError(ErrorTag.PplWidgetRuntimeError)<{
  message: string;
  cause?: unknown;
}> {}

export type PplWidgetError =
  | PplWidgetNotLoadedError
  | PplWidgetCancelledError
  | PplWidgetAssetLoadError
  | PplWidgetPayloadError
  | PplWidgetRuntimeError;

export type PplWidgetOptions = {
  readonly embedSelector: string;
  readonly apiKey: string;
  readonly language: string;
  readonly country: string;
  readonly lat?: number;
  readonly lng?: number;
  readonly signal?: AbortSignal;
  /** A browser test uses a local loader fixture. */
  readonly loaderUrl?: string;
};

export type PplWidgetService = {
  readonly pick: (options: PplWidgetOptions) => Effect.Effect<PickupPoint, PplWidgetError>;
};

export class PplWidget extends Context.Service<PplWidget, PplWidgetService>()(
  ContextTag.PplWidget
) {}
